import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { selectAccount } from "../features/account/accountSlice";
import { ROUTE_PATHS } from "../library/helper";

// Awareness-only reminder (ISO 27001:2022 A.5.17). Non-blocking:
// login still succeeds, no API is gated. Dismissible per browser
// session; reappears next login.
const REMINDER_THRESHOLD_DAYS = 90;
const DISMISS_KEY = "trucount_pwd_reminder_dismissed";

function daysSince(isoOrDate) {
    if (!isoOrDate) return null;
    const then = new Date(isoOrDate);
    if (Number.isNaN(then.getTime())) return null;
    const ms = Date.now() - then.getTime();
    if (ms < 0) return 0;
    return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export default function PasswordAgeBanner() {
    const { t } = useTranslation();
    const account = useSelector(selectAccount);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    }, []);

    const ageDays = useMemo(
        () => daysSince(account?.user_password_updated_at),
        [account?.user_password_updated_at]
    );

    if (dismissed) return null;
    if (ageDays == null) return null;
    if (ageDays < REMINDER_THRESHOLD_DAYS) return null;

    const handleDismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, "1");
        setDismissed(true);
    };

    return (
        <div
            role="status"
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{
                background: "#FEF3C7",
                border: "1px solid #F59E0B",
                borderRadius: 8,
                color: "#78350F",
                fontSize: "0.9rem",
            }}
        >
            <span>
                {t(
                    "passwordAge.reminder",
                    "Your password hasn't been updated in over 3 months. Please consider updating it."
                )}
            </span>
            <span className="d-flex align-items-center" style={{ gap: 12 }}>
                <Link
                    to={ROUTE_PATHS.settingChangePassword}
                    style={{ color: "#0A564B", fontWeight: 600 }}
                >
                    {t("passwordAge.changeNow", "Change password")}
                </Link>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label={t("passwordAge.dismiss", "Dismiss")}
                    className="btn btn-link p-0"
                    style={{ color: "#78350F", textDecoration: "none", fontSize: "1.1rem", lineHeight: 1 }}
                >
                    &times;
                </button>
            </span>
        </div>
    );
}
